/**
 * Confirmed-price filter (2026-08-21, confirmed-price gate Phase 1).
 *
 * Mirrors the server-side confirmed-price gate in
 * api/app/services/search/reconciler.py::is_confirmed_offer. The server
 * marks an offer `price_confirmed=false` when its price is UNCONFIRMED -
 * a suspiciously-deep outlier below a peer floor, not corroborated by
 * the page's machine-readable structured data, and pulled from visible
 * text rather than JSON-LD. Background: Amazon served our fetcher a bot
 * page whose only price was a used/secondary $1,047 for a NEW MacBook
 * that really costs $2,099; that number won the comparison at 0.9
 * self-reported confidence.
 *
 * Only a CONFIRMED price may be shown as a price or crowned "best deal",
 * so we drop unconfirmed offers before the min-sort AND before rendering
 * the rows - so the page's own winner-pick agrees with the server and
 * the fake number is never displayed at all.
 *
 * A missing/undefined `price_confirmed` means CONFIRMED (`!== false`),
 * so when the backend gate is OFF every offer is confirmed and this is a
 * no-op (byte-for-byte today's behaviour). Mirrors the "keep the
 * original list if the filter would empty it" fallback in
 * dropCarrierOffers so an all-unconfirmed result still renders.
 *
 * Real-deal plan Part B (2026-09-29, the owner's requirement 1: show
 * every shop Google shows): a row from Google's list (`row_state ===
 * "listed"`) carries Google's price, which we did not read on the shop's
 * page, so the server marks it unconfirmed - but it is not a bot-page
 * outlier, it is a shop the shopper must see. It is kept. The August
 * rule stays for every read row exactly as before.
 */

/** True when the offer's price is confirmed (or the field is absent). */
export function isConfirmedPrice(product: {
  price_confirmed?: boolean | null;
}): boolean {
  return product?.price_confirmed !== false;
}

/** True when the row keeps its price on the page: a confirmed price, or a
 *  row from Google's list (its price is Google's, shown as such). */
export function isShownPrice(product: {
  price_confirmed?: boolean | null;
  row_state?: string | null;
}): boolean {
  return isConfirmedPrice(product) || product?.row_state === "listed";
}

/**
 * Drop unconfirmed-price offers from a flat product list. Returns the
 * original list unchanged if filtering would leave nothing (defensive:
 * an all-unconfirmed result should still render something).
 */
export function dropUnconfirmedPrices<
  T extends { price_confirmed?: boolean | null; row_state?: string | null },
>(products: T[]): T[] {
  const kept = products.filter(isShownPrice);
  return kept.length > 0 ? kept : products;
}
